import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { DocumentDqMapping } from "./documentDqMappingsApi";

type DqMappingNote = "OK" | "FAIL" | "NA";

interface PrintPreviewDocumentDqMappingsProps {
    mappings: DocumentDqMapping[];
    lang?: "pl" | "en";
}

const T = (lang: "pl" | "en") =>
    lang === "pl"
        ? {
            title: "Mapowanie URS z dokumentacją dostawcy",
            reqCode: "Nr",
            reqDesc: "Wymaganie",
            noteOnly: "Ocena",
            groupMapping: "Mapowanie dokumentacji dostawcy",
            note_ok: "Spełnia",
            note_fail: "Nie spełnia",
            note_na: "Nie dotyczy",
            dash: "—",
        }
        : {
            title: "Mapping URS with supplier documentation",
            reqCode: "No",
            reqDesc: "Requirement",
            noteOnly: "Assessment",
            groupMapping: "Documentation mapping",
            note_ok: "Compliant",
            note_fail: "Non-compliant",
            note_na: "N/A",
            dash: "—",
        };

const getNoteConfig = (
    n: DqMappingNote | undefined,
    lang: "pl" | "en"
) => {
    const label = noteLabel(n, lang);

    if (!n) {
        return { label: "", color: "#000000", bg: "#ffffff", symbol: "" };
    }

    switch (n) {
        case "OK":
            return {
                label,
                color: "#1b5e20",   // ciemna zieleń
                bg: "#c8e6c9",      // jasna zieleń
                symbol: "✔",
            };
        case "FAIL":
            return {
                label,
                color: "#b71c1c",   // ciemna czerwień
                bg: "#ffcdd2",      // jasna czerwień
                symbol: "✖",
            };
        case "NA":
        default:
            return {
                label,
                color: "#000000",   // czarny
                bg: "#eeeeee",      // jasne szare
                symbol: "—",
            };
    }
};


const noteLabel = (n: DqMappingNote | undefined, lang: "pl" | "en") => {
    if (!n) return "";
    const t = T(lang);
    return n === "OK" ? t.note_ok : n === "FAIL" ? t.note_fail : t.note_na;
};

export const PrintPreviewDocumentDqMappingsView: React.FC<
    PrintPreviewDocumentDqMappingsProps
> = ({ mappings, lang = "pl" }) => {
    const t = T(lang);
    const Dash = t.dash;

    const rows = useMemo(
        () => [...(mappings ?? [])].sort((a, b) => a.order - b.order),
        [mappings]
    );

    return (
        <Box
            className="print-root"
            sx={{
                p: "2px",
                backgroundColor: "#fff",
                fontFamily: `Arial, "Helvetica Neue", Helvetica, sans-serif`,
                lineHeight: 1.4,
                color: "#000",
                "& table": {
                    width: "100%",
                    marginBottom: "16px",
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                },
                "& th, & td": {
                    fontSize: "0.9rem",
                    verticalAlign: "top",
                    padding: "4px 6px",
                    border: "1px solid #000",
                    wordBreak: "break-word",
                },
                "@media print": {
                    border: "none",
                    p: 0,
                },
            }}
        >
            <section>
                <table aria-label="dq-mappings">
                    <colgroup>
                        {/* Nr URS */}
                        <col style={{ width: 50, maxWidth: 50 }} />
                        {/* Lp dokumentu */}
                        <col style={{ width: 50, maxWidth: 50 }} />
                        {/* Etykieta pola */}
                        <col style={{ width: "20%" }} />
                        {/* Wartość pola */}
                        <col style={{ width: "auto" }} />
                        {/* Ocena */}
                        <col style={{ width: 150, maxWidth: 150 }} />
                    </colgroup>

                    <tbody>
                        {/* Tytuł tabeli */}
                        <tr>
                            <td
                                colSpan={5}
                                style={{
                                    background: "#f0f0f0",
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                }}
                            >
                                {t.title}
                            </td>
                        </tr>

                        {/* Nagłówki: Nr | Wymaganie | Ocena (Wymaganie ma colspan=3) */}
                        <tr>
                            <td
                                style={{
                                    background: "#f0f0f0",
                                    fontWeight: 700,
                                }}
                            >
                                {t.reqCode}
                            </td>
                            <td
                                colSpan={3}
                                style={{
                                    background: "#f0f0f0",
                                    fontWeight: 700,
                                }}
                            >
                                {t.reqDesc}
                            </td>
                            <td
                                style={{
                                    background: "#f0f0f0",
                                    fontWeight: 700,
                                }}
                            >
                                {t.noteOnly}
                            </td>
                        </tr>

                        {rows.length ? (
                            rows.map((m, rowIdx) => {
                                const reqNo =
                                    m.requirementNumber &&
                                        m.requirementNumber.trim().length
                                        ? m.requirementNumber
                                        : Dash;
                                const reqHtml =
                                    m.requirementDescription &&
                                        m.requirementDescription.trim().length
                                        ? m.requirementDescription
                                        : Dash;

                                const docs = (m.docMappings ?? [])
                                    .slice()
                                    .sort((a, b) => a.order - b.order);

                                const note = (m as any).note as DqMappingNote | undefined;
                                const noteCfg = getNoteConfig(note, lang);

                                return (
                                    <React.Fragment
                                        key={m.id ?? `${m.guid}-${rowIdx}`}
                                    >
                                        {/* Wiersz 1 – wymaganie */}
                                        <tr>
                                            <td
                                                style={{
                                                    fontSize: "0.8rem",
                                                    lineHeight: 1.2,
                                                    letterSpacing: -0.2,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {reqNo}
                                            </td>
                                            <td colSpan={3}>
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: reqHtml,
                                                    }}
                                                />
                                            </td>
                                            <td
                                                style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {noteCfg.label ? (
                                                    <span
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 6,
                                                            fontWeight: 700,
                                                            color: noteCfg.color,
                                                            fontSize: "0.9rem",
                                                        }}
                                                    >
                                                        <span>{noteCfg.symbol}</span>
                                                        <span>{noteCfg.label}</span>
                                                    </span>
                                                ) : (
                                                    "\u00A0"
                                                )}
                                            </td>

                                        </tr>

                                        {/* Mapowanie dokumentacji dostawcy */}
                                        {docs.length ? (
                                            <>
                                                {/* Wiersz tytułowy sekcji mapowania */}
                                                <tr>
                                                    <td
                                                        style={{
                                                            borderBottom:
                                                                "1px solid #fff",
                                                        }}
                                                    />
                                                    <td
                                                        colSpan={4}
                                                        style={{
                                                            background: "#eaeaea",
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {t.groupMapping}
                                                    </td>
                                                </tr>

                                                {docs.map((d, idx) => {
                                                    const number =
                                                        d.docNumber &&
                                                            d.docNumber.trim()
                                                                .length
                                                            ? d.docNumber
                                                            : Dash;
                                                    const title =
                                                        d.docTitle &&
                                                            d.docTitle.trim()
                                                                .length
                                                            ? d.docTitle
                                                            : Dash;
                                                    const hasDesc =
                                                        !!d.docDescription &&
                                                        d.docDescription.trim()
                                                            .length > 0;
                                                    const hasComment =
                                                        !!d.mappingComment &&
                                                        d.mappingComment.trim()
                                                            .length > 0;

                                                    return (
                                                        <React.Fragment
                                                            key={
                                                                d.id ??
                                                                `doc-${rowIdx}-${idx}`
                                                            }
                                                        >
                                                            {/* Wiersz: Lp / Nr */}
                                                            <tr>
                                                                {/* puste Nr URS */}
                                                                <td
                                                                    style={{
                                                                        borderBottom:
                                                                            "1px solid #fff",
                                                                    }}
                                                                />
                                                                <td
                                                                    style={{
                                                                        textAlign:
                                                                            "right",
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                        background:
                                                                            "#eaeaea",
                                                                        borderBottom:
                                                                            "1px solid #eaeaea",
                                                                    }}
                                                                >
                                                                    {idx + 1}.
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        fontWeight: 700,
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                        background:
                                                                            "#f7f7f7",
                                                                    }}
                                                                >
                                                                    Nr:
                                                                </td>
                                                                <td colSpan={2}>
                                                                    {number}
                                                                </td>
                                                            </tr>

                                                            {/* Wiersz: (puste) / Tytuł */}
                                                            <tr>
                                                                <td
                                                                    style={{
                                                                        borderBottom:
                                                                            "1px solid #fff",
                                                                    }}
                                                                />
                                                                <td
                                                                    style={{
                                                                        background:
                                                                            "#eaeaea",
                                                                        borderBottom:
                                                                            "1px solid #eaeaea",
                                                                    }}
                                                                />
                                                                <td
                                                                    style={{
                                                                        fontWeight: 700,
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                        background:
                                                                            "#f7f7f7",
                                                                    }}
                                                                >
                                                                    Tytuł:
                                                                </td>
                                                                <td colSpan={2}>
                                                                    {title}
                                                                </td>
                                                            </tr>

                                                            {/* Wiersz: (puste) / Opis */}
                                                            <tr>
                                                                <td
                                                                    style={{
                                                                        borderBottom:
                                                                            "1px solid #fff",
                                                                    }}
                                                                />
                                                                <td
                                                                    style={{
                                                                        background:
                                                                            "#eaeaea",
                                                                        borderBottom:
                                                                            "1px solid #eaeaea",
                                                                    }}
                                                                />
                                                                <td
                                                                    style={{
                                                                        fontWeight: 700,
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                        background:
                                                                            "#f7f7f7",
                                                                    }}
                                                                >
                                                                    Opis:
                                                                </td>
                                                                <td colSpan={2}>
                                                                    {hasDesc ? (
                                                                        <div
                                                                            dangerouslySetInnerHTML={{
                                                                                __html:
                                                                                    d.docDescription!,
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        Dash
                                                                    )}
                                                                </td>
                                                            </tr>

                                                            {/* Wiersz: (puste) / Komentarz */}
                                                            <tr>
                                                                <td
                                                                    style={{
                                                                        borderBottom:
                                                                            "1px solid #fff",
                                                                    }}
                                                                />
                                                                <td
                                                                    style={{
                                                                        background:
                                                                            "#eaeaea",
                                                                        borderBottom:
                                                                            "1px solid #000",
                                                                    }}
                                                                />
                                                                <td
                                                                    style={{
                                                                        fontWeight: 700,
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                        background:
                                                                            "#f7f7f7",
                                                                    }}
                                                                >
                                                                    Komentarz:
                                                                </td>
                                                                <td colSpan={2}>
                                                                    {hasComment
                                                                        ? d.mappingComment
                                                                        : Dash}
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    );
                                                })}

                                                <tr>
                                                    <td
                                                        colSpan={5}
                                                        style={{
                                                            background:
                                                                "#eaeaea",
                                                        }}
                                                    />
                                                </tr>
                                            </>
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan={5}
                                                    style={{
                                                        background: "#eaeaea",
                                                    }}
                                                />
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <tr>
                                <td
                                    colSpan={5}
                                    style={{ background: "#eaeaea" }}
                                />
                            </tr>
                        )}
                    </tbody>
                </table>
            </section>
        </Box>
    );
};
